import React, { useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DataTableProps, SortType } from './types';
import { getColumnLabel } from 'components/common/grid';
import IconMi from 'components/common/icon/IconMi';
import { formatCellValue } from './utils';
import { TableContainer, TableHeader, HeaderCell, TableBody, Row, Cell, ResizeHandle, ResizeSpanner } from './styles';
import { useVirtualization } from './hooks/useVirtualization';
import { useColumnResize } from './hooks/useColumnResize';
import CellWithCopy from './CellWithCopy';
import LinkCell from 'components/common/grid/cell/LinkCell';
import TextCell from 'components/common/grid/cell/TextCell';
import CopyTextCell from 'components/common/grid/cell/CopyTextCell';
import CopyCell from 'components/common/grid/cell/CopyCell';
import TagCell from 'components/common/grid/cell/TagCell';
import SelectedCell from 'components/common/grid/cell/SelectedCell';
import PreCell from 'components/common/grid/cell/PreCell';
import AsyncTooltipCell from 'components/common/grid/cell/AsyncTooltipCell';
import HistoryCell from 'components/common/grid/cell/HistoryCell';

const CELL_COMPONENTS: Record<string, React.ComponentType<any>> = {
    link: LinkCell,
    text: TextCell,
    copyText: CopyTextCell,
    copy: CopyCell,
    tag: TagCell,
    selected: SelectedCell,
    preCell: PreCell,
    asyncTooltipCell: AsyncTooltipCell,
    history: HistoryCell,
};

export default function DataTable({
    data,
    columns,
    t,
    height = 600,
    rowHeight = 32,
    headerHeight = 32,
    maxVisibleColumns = 20,
    emptyStateMessage,
    tableName = 'data-table',
    idColumn = 'id',
    onSortColumn,
    orderBy,
    order,
    columnWidths: externalColumnWidths,
    onColumnResize,
}: DataTableProps) {
    const { customWidths, startResize, handleMouseMove, stopResize, isResizing, spannerPosition } = useColumnResize({
        externalWidths: externalColumnWidths,
        onColumnResize,
    });

    const { containerRef, totalHeight, totalWidth, visibleRows, visibleColumns, columnWidths, handleScroll } =
        useVirtualization({ data, columns, rowHeight, headerHeight, maxVisibleColumns, customWidths });

    useEffect(() => {
        if (isResizing) {
            const originalUserSelect = document.body.style.userSelect;
            const originalCursor = document.body.style.cursor;
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', stopResize);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', stopResize);
                document.body.style.userSelect = originalUserSelect;
                document.body.style.cursor = originalCursor;
            };
        }
    }, [isResizing, handleMouseMove, stopResize]);

    const getSpannerPosition = () => {
        if (spannerPosition === null || !containerRef.current) return null;
        const containerRect = containerRef.current.getBoundingClientRect();
        return spannerPosition - containerRect.left + containerRef.current.scrollLeft;
    };

    if (!data || data.length === 0) {
        return (
            <TableContainer ref={containerRef} sx={{ height }} data-test={`grid_${tableName}_empty`}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'text.secondary',
                    }}
                >
                    <Typography variant="body2">{emptyStateMessage}</Typography>
                </Box>
            </TableContainer>
        );
    }

    return (
        <TableContainer ref={containerRef} sx={{ height }} onScroll={handleScroll} data-test={`grid_${tableName}_body`}>
            <TableHeader sx={{ height: headerHeight, width: totalWidth }} data-test={`grid_${tableName}_header`}>
                <Box sx={{ width: visibleColumns.offsetLeft }} />
                {visibleColumns.columns.map((column: any, index: number) => {
                    const isSortable = column.sortable !== false && onSortColumn;
                    const isSorted = orderBy === column.name;
                    const handleSort = () => {
                        if (!isSortable) return;
                        const newSortType: SortType = isSorted && order === 'asc' ? 'desc' : 'asc';
                        onSortColumn(column.name, newSortType);
                    };

                    return (
                        <HeaderCell
                            key={column.name}
                            sx={{
                                width: columnWidths[index],
                                cursor: isSortable ? 'pointer' : 'default',
                                '&:hover .sort-icon': isSortable
                                    ? {
                                          opacity: 1,
                                      }
                                    : {},
                            }}
                            data-test={`grid_${tableName}_${column.name}_column_header`}
                        >
                            <Stack
                                direction={'row'}
                                justifyContent={'space-between'}
                                alignItems={'center'}
                                width={'100%'}
                            >
                                <Box component="span" sx={{ flex: 1 }} onClick={handleSort}>
                                    {getColumnLabel(column, t)}
                                </Box>
                                {isSortable && isSorted && (
                                    <IconMi
                                        icon={'arrow-right'}
                                        fontSize={'16'}
                                        sx={{ transform: order === 'desc' ? 'rotate(90deg)' : 'rotate(-90deg)' }}
                                    />
                                )}
                            </Stack>
                            <ResizeHandle
                                className={`resize-handle ${isResizing ? 'resizing' : ''}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    startResize(column.name, e.clientX, columnWidths[index]);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                data-test={`grid_${tableName}_${column.name}_resize_handle`}
                            />
                        </HeaderCell>
                    );
                })}
            </TableHeader>

            <TableBody sx={{ height: totalHeight, width: totalWidth }}>
                {visibleRows.rows.map((row, index) => {
                    const rowIndex = visibleRows.startIndex + index;
                    const rowId = row[idColumn] ?? rowIndex;
                    return (
                        <Row
                            key={rowIndex}
                            sx={{
                                top: rowIndex * rowHeight,
                                height: rowHeight,
                            }}
                            data-test={`grid_${tableName}_row_${rowId}`}
                        >
                            <Box sx={{ width: visibleColumns.offsetLeft }} />
                            {visibleColumns.columns.map((column: any, colIndex: number) => {
                                const cellValue = row[column.name];
                                const CellComponent =
                                    cellValue && typeof cellValue === 'object'
                                        ? CELL_COMPONENTS[cellValue.component]
                                        : undefined;

                                return (
                                    <Cell
                                        className="rs-table-cell"
                                        key={column.name}
                                        sx={{ width: columnWidths[colIndex] }}
                                        data-test={`grid_${tableName}_${column.name}_column_${rowId}_cell`}
                                    >
                                        {CellComponent ? (
                                            <CellComponent {...cellValue.config} t={t} />
                                        ) : (
                                            <CellWithCopy
                                                value={cellValue?.config?.value ?? cellValue}
                                                displayValue={formatCellValue(cellValue)}
                                            />
                                        )}
                                    </Cell>
                                );
                            })}
                        </Row>
                    );
                })}
            </TableBody>
            {getSpannerPosition() !== null && (
                <ResizeSpanner className="active" sx={{ left: `${getSpannerPosition()}px` }} />
            )}
        </TableContainer>
    );
}
